// A live mock of the public intake form rendered with the draft palette so
// admins see exactly what visitors will before saving. Fidelity comes from
// reusing the real building blocks — buildTheme + a nested ThemeProvider, the
// real LocationHighlightSvg, ProductCheckboxRowView, and
// EstimateSummaryPanelView — rather than hand-painted approximations.
//
// Every region of the mock is wrapped in a clickable Zone: clicking reports
// the palette tokens that paint it (via onSelectTokens) so the editor can
// spotlight the matching color fields. The real components inside zones are
// made inert (pointer-events: none) so clicks always select tokens instead of
// toggling controls.
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { buildTheme } from '../../../theme.js';
import { isHexColor } from '../../../utils/wcag-contrast.js';
import { regionCountiesSvg } from '../../../utils/location-highlight/region-counties-svg.js';
import { svgPathBounds, unprojectPoint } from '../../../utils/location-highlight/project-geojson.js';
import LocationHighlightSvg from '../../location-highlight/LocationHighlightSvg.jsx';
import ProductCheckboxRowView from '../../products/ProductCheckboxRowView.jsx';
import EstimateSummaryPanelView from '../../estimate/EstimateSummaryPanelView.jsx';
import SanbornLogo from '../../SanbornLogo.jsx';

// A county to highlight so the selection reads at preview size.
const HIGHLIGHT_FIPS = '001';

// A small sample topo-tile grid so the preview shows the tile-grid overlay and
// legend exactly as the live map does. Built by reverse-projecting the
// highlighted county's baked path bounds back to lng/lat, then laying a grid of
// whole tiles across that box — so it tracks whatever region is configured
// without shipping any real tile data into the editor.
const sampleTileGeoJson = (() => {
  const { projection, counties } = regionCountiesSvg;
  const county = counties.find((c) => c.fips === HIGHLIGHT_FIPS) ?? counties[0];
  const bounds = county && svgPathBounds(county.path);
  if (!bounds) return null;
  const [lngMin, latMax] = unprojectPoint([bounds.minX, bounds.minY], projection);
  const [lngMax, latMin] = unprojectPoint([bounds.maxX, bounds.maxY], projection);
  const cols = 4;
  const rows = 4;
  const dLng = (lngMax - lngMin) / cols;
  const dLat = (latMax - latMin) / rows;
  const features = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const x0 = lngMin + c * dLng;
      const x1 = x0 + dLng;
      const y0 = latMin + r * dLat;
      const y1 = y0 + dLat;
      features.push({
        type: 'Feature',
        properties: { id: `sample-${r}-${c}`, topoName: `Quad ${r}${c}` },
        geometry: {
          type: 'Polygon',
          coordinates: [[[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]]],
        },
      });
    }
  }
  return JSON.stringify({ type: 'FeatureCollection', features });
})();

// Static fallbacks if the live catalog hasn't loaded.
const SAMPLE_PRODUCTS = [
  {
    id: 'sample-1',
    name: 'Orthoimagery',
    short_description: 'Aerial base map imagery.',
    selection_mode: 'single',
    icon_key: 'satellite_alt',
  },
  {
    id: 'sample-2',
    name: 'LiDAR / elevation',
    short_description: 'Terrain and surface models.',
    selection_mode: 'single',
    icon_key: 'terrain',
  },
];
const SAMPLE_OPTIONS = [
  {
    id: 'sample-opt-1', product_id: 'sample-1', name: 'Standard', short_description: '6-inch resolution.',
  },
  {
    id: 'sample-opt-2', product_id: 'sample-1', name: 'High', short_description: '3-inch resolution.',
  },
];

const SAMPLE_AREA = { areaSqMi: 604, estPopulation: 58805 };
const SAMPLE_ESTIMATE = { loading: false, error: null, data: { low: 84000, high: 114000 } };

// Clickable region of the mock. Clicks bubble up from the real components
// (whose controls are controlled-but-handlerless here, so they can't toggle)
// to the nearest Zone; stopPropagation keeps nested zones from also
// triggering their parents.
const Zone = ({
  tokens, onSelectTokens, sx, children,
}) => (
  <Box
    onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
      onSelectTokens?.(tokens);
    }}
    sx={{
      cursor: 'pointer',
      position: 'relative',
      '&:hover': { outline: '2px dashed rgba(127,127,127,0.85)', outlineOffset: 2 },
      // Keep form controls from looking interactive inside the mock.
      '& *': { cursor: 'pointer !important' },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const BrandingPreview = ({
  palette, mode, content = {}, onSelectTokens,
}) => {
  // Only pass valid hexes to buildTheme — a half-typed value must never crash
  // MUI's color augmentation; the field just falls back to the default until
  // it parses.
  const theme = useMemo(() => {
    const clean = Object.fromEntries(
      Object.entries(palette ?? {}).filter(([, v]) => isHexColor(v)),
    );
    return buildTheme(mode, clean);
  }, [mode, palette]);

  // Use the real catalog when it's loaded so the preview shows actual content.
  const catalog = useSelector((s) => s.catalog.data);
  const { products, options } = useMemo(() => {
    const catProducts = (catalog?.products ?? []).slice(0, 2);
    if (catProducts.length === 0) {
      return { products: SAMPLE_PRODUCTS, options: SAMPLE_OPTIONS };
    }
    const ids = new Set(catProducts.map((p) => p.id));
    return {
      products: catProducts,
      options: (catalog?.options ?? []).filter((o) => ids.has(o.product_id)),
    };
  }, [catalog]);

  const selected = products[0];
  const selectedOptions = options.filter((o) => o.product_id === selected.id);

  return (
    <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'flex-start' }}>
      <ThemeProvider theme={theme}>
        <Zone
          tokens={['bg']}
          onSelectTokens={onSelectTokens}
          sx={{
            bgcolor: 'background.default',
            // Typography inherits color (only Paper/CssBaseline reset it), so
            // anchor the preview to the draft theme's text color — otherwise
            // headings inherit the admin page's body color and the wrong mode
            // bleeds through.
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          {/* Header — same structure as the intake page header */}
          <Zone
            tokens={['surface', 'text', 'textDim']}
            onSelectTokens={onSelectTokens}
            sx={{
              px: 2,
              pt: 1.5,
              pb: 1,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <Box sx={{
              color: 'text.primary', display: 'flex', alignItems: 'center', flexShrink: 0,
            }}
            >
              <SanbornLogo height={48} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700, mb: 0.25 }}>
                {content.intakeHeaderTitle || 'Statewide Pricing Portal'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {content.intakeHeaderSubtitle || 'Select an area and products to get an estimate.'}
              </Typography>
            </Box>
          </Zone>

          <Box sx={{
            display: 'flex', gap: 1.5, p: 1.5, alignItems: 'flex-start',
          }}
          >
            {/* Area column with the real map component */}
            <Zone
              tokens={['surface', 'mapBackdrop', 'mapLand', 'mapLandStroke', 'mapHighlight', 'mapTopoGrid']}
              onSelectTokens={onSelectTokens}
              sx={{
                flex: '0 0 36%',
                minWidth: 0,
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                p: 1,
              }}
            >
              <LocationHighlightSvg
                areaType="county"
                countyFips={HIGHLIGHT_FIPS}
                tileGeoJson={sampleTileGeoJson}
                name="Autauga County"
                regionName="the region"
              />
            </Zone>

            {/* Product column with the real checkbox rows */}
            <Box sx={{
              flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1,
            }}
            >
              <Zone tokens={['textDim']} onSelectTokens={onSelectTokens} sx={{ px: 0.5 }}>
                <Typography variant="overline" color="text.secondary">Products</Typography>
              </Zone>
              <Zone tokens={['primary', 'surface', 'text', 'textDim', 'surfaceRaised']} onSelectTokens={onSelectTokens}>
                <ProductCheckboxRowView
                  product={selected}
                  options={selectedOptions}
                  isChecked
                  selectedOptionIds={selectedOptions.length ? [selectedOptions[0].id] : []}
                />
              </Zone>
              {products[1] && (
                <Zone tokens={['surface', 'text', 'textDim']} onSelectTokens={onSelectTokens}>
                  <ProductCheckboxRowView
                    product={products[1]}
                    options={[]}
                    isChecked={false}
                  />
                </Zone>
              )}
            </Box>

            {/* Estimate panel with the real summary component */}
            <Zone
              tokens={['surface', 'text', 'textDim', 'primary']}
              onSelectTokens={onSelectTokens}
              sx={{ flex: '0 0 30%', minWidth: 0 }}
            >
              <EstimateSummaryPanelView
                area={SAMPLE_AREA}
                estimate={SAMPLE_ESTIMATE}
                selectedCount={1}
              />
            </Zone>
          </Box>
        </Zone>
      </ThemeProvider>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
      >
        Preview — click any part to highlight its color fields
      </Typography>
    </Box>
  );
};

export default BrandingPreview;
